/**
 * Send Braze campaign after successful payment.
 * Proxies to WordPress: /wp-admin/admin-ajax.php?action=send_braze_campaign
 */

import { formatPhoneForPayment } from './formatters'
import { logger } from './logger'
import { localStore } from './localStorageKey'

/** Braze campaign triggered after first purchase for CV-1 / CV-2 / NAD / Sermorelin funnels */
export const BRAZE_POST_PURCHASE_CAMPAIGN_ID = '623881db-7c52-4a3e-a9e9-6938ef4219bb'

const ELIGIBLE_FUNNEL_IDS = new Set([
  'variant-cv-1',
  'variant-cv-2',
  'variant-cv-3',
  'variant-cv-4',
  'variant-cv-nad',
  'variant-cv-sermorelin',
])

const DEDUP_KEY_PREFIX = 'braze_campaign_sent_'

/**
 * Build Braze campaign recipients payload from current patient / funnel data.
 * @param {Object} funnelData
 * @param {string} caseId
 * @returns {Object}
 */
export const buildBrazeCampaignPayload = (funnelData, caseId) => {
  const email = funnelData.email || ''
  const phone = formatPhoneForPayment(funnelData.phone || '')

  return {
    campaign_id: BRAZE_POST_PURCHASE_CAMPAIGN_ID,
    recipients: [
      {
        email: "help@sunlight.com",
        prioritization: ["identified", "most_recently_updated"],
        send_to_existing_only: false,
        attributes: {
          email,
        },
        trigger_properties: {
          purchase_date: new Date().toISOString(),
          first_name: funnelData.first_name || '',
          last_name: funnelData.last_name || '',
          phone_number: phone,
          email_address: email,
          gender: funnelData.gender || '',
          case_id: caseId || funnelData.case_id || '',
        },
      },
    ],
  }
}

/**
 * Fire Braze post-purchase campaign for eligible funnels.
 * Safe to call multiple times — deduped per case_id via localStorage.
 *
 * @param {Object} funnelData - Current funnel data
 * @param {string} caseId - CareValidate case ID from successful payment
 * @returns {Promise<Object|undefined>}
 */
export const sendBrazeCampaign = async (funnelData, caseId) => {
  const funnelId = funnelData?.funnel_id || ''
  if (!ELIGIBLE_FUNNEL_IDS.has(funnelId)) {
    return
  }

  const resolvedCaseId = caseId || funnelData?.case_id
  if (!funnelData?.email || !resolvedCaseId) {
    logger.warn('[Braze Campaign] Skipped: missing email or case_id')
    return
  }

  const dedupKey = `${DEDUP_KEY_PREFIX}${resolvedCaseId}`
  if (localStore.getItem(dedupKey)) {
    logger.debug('[Braze Campaign] Already sent for case_id:', resolvedCaseId)
    return
  }

  try {
    const payload = buildBrazeCampaignPayload(funnelData, resolvedCaseId)
    logger.debug('[Braze Campaign] Sending for case_id:', resolvedCaseId)

    const response = await fetch('/send-braze-campaign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    const result = await response.json()
    localStore.setItem(dedupKey, 'true')
    logger.debug('[Braze Campaign] Sent successfully:', result)
    return result
  } catch (error) {
    logger.error('[Braze Campaign] Error sending campaign:', error)
    // Never throw — must not interrupt post-payment flow
  }
}

export default sendBrazeCampaign
